import type { PlayEvent, PlayEventMatch, PlayEventRound, PlayEventStatus } from '../types/playEvent';
import { PUBLIC_JOINABLE_PLAY_EVENT_STATUSES } from '../types/playEvent';

type DateLike = Date | string;

type PlayEventVisibilitySchedule = Pick<
  PlayEvent,
  | 'matchupVisibilityMinutes'
  | 'startsAt'
  | 'endsAt'
  | 'roundCount'
  | 'roundDurationMinutes'
  | 'roundBreakMinutes'
>;

export type PlayEventRoundTimingContext = Pick<
  PlayEvent,
  'startsAt' | 'endsAt' | 'roundCount' | 'roundDurationMinutes' | 'roundBreakMinutes'
>;

function toDate(value: DateLike): Date {
  return value instanceof Date ? value : new Date(value);
}

function toDateOrNull(value: DateLike | null | undefined): Date | null {
  if (value == null) return null;
  return toDate(value);
}

/** Prefer the live event schedule so visibility stays aligned after reschedules. */
export function resolveMatchScheduledStartForVisibility(
  match: Pick<PlayEventMatch, 'roundNumber' | 'scheduledStart'>,
  round: Pick<PlayEventRound, 'startsAt' | 'roundNumber'> | null,
  event: PlayEventVisibilitySchedule
): Date | null {
  if (
    event.roundCount &&
    event.roundCount >= match.roundNumber &&
    event.startsAt &&
    event.endsAt
  ) {
    const roundStarts = computeRoundStartTimes(
      toDate(event.startsAt),
      toDate(event.endsAt),
      event.roundCount,
      event.roundDurationMinutes,
      event.roundBreakMinutes
    );
    const computed = roundStarts[match.roundNumber - 1];
    if (computed) return computed;
  }

  return toDateOrNull(match.scheduledStart ?? round?.startsAt ?? null);
}

/** Prefer the live event schedule so round labels stay aligned after reschedules. */
export function resolveRoundStartTime(
  round: Pick<PlayEventRound, 'startsAt' | 'roundNumber'> | null,
  roundNumber: number,
  event: PlayEventRoundTimingContext
): Date | null {
  if (
    event.roundCount &&
    event.roundCount >= roundNumber &&
    event.startsAt &&
    event.endsAt
  ) {
    const roundStarts = computeRoundStartTimes(
      toDate(event.startsAt),
      toDate(event.endsAt),
      event.roundCount,
      event.roundDurationMinutes,
      event.roundBreakMinutes
    );
    const computed = roundStarts[roundNumber - 1];
    if (computed) return computed;
  }

  return toDateOrNull(round?.startsAt ?? null);
}

export function isMatchVisibleToPlayer(
  match: Pick<PlayEventMatch, 'roundNumber' | 'scheduledStart' | 'result'>,
  round: Pick<PlayEventRound, 'startsAt' | 'roundNumber'> | null,
  event: PlayEventVisibilitySchedule,
  now: Date = new Date()
): boolean {
  if (match.result !== 'unplayed' && match.result !== 'bye') {
    return true;
  }

  const visibilityMs = event.matchupVisibilityMinutes * 60 * 1000;
  const scheduledStart = resolveMatchScheduledStartForVisibility(match, round, event);

  if (!scheduledStart) return true;
  return now.getTime() >= scheduledStart.getTime() - visibilityMs;
}

export function filterVisibleMatches<T extends PlayEventMatch>(
  matches: T[],
  roundsByNumber: Map<number, PlayEventRound>,
  event: PlayEventVisibilitySchedule,
  now: Date = new Date()
): T[] {
  return matches.filter((match) => {
    const round = roundsByNumber.get(match.roundNumber) ?? null;
    return isMatchVisibleToPlayer(match, round, event, now);
  });
}

export function findNextRoundBoardReveal(
  rounds: PlayEventRound[],
  matches: PlayEventMatch[],
  event: PlayEventVisibilitySchedule,
  now: Date = new Date()
): { roundNumber: number; revealAt: Date } | null {
  const roundsByNumber = new Map(rounds.map((round) => [round.roundNumber, round]));
  const roundNumbers = [...new Set(rounds.map((round) => round.roundNumber))].sort((a, b) => a - b);

  for (const roundNumber of roundNumbers) {
    const round = roundsByNumber.get(roundNumber);
    if (!round) continue;

    const roundMatches = matches.filter((match) => match.roundNumber === roundNumber);
    if (roundMatches.length === 0) continue;

    const hasHiddenMatch = roundMatches.some(
      (match) => !isMatchVisibleToPlayer(match, round, event, now)
    );
    if (!hasHiddenMatch) continue;

    let earliestReveal: Date | null = null;
    for (const match of roundMatches) {
      if (match.result !== 'unplayed' && match.result !== 'bye') continue;

      const scheduledStart = resolveMatchScheduledStartForVisibility(match, round, event);
      if (!scheduledStart) continue;

      const revealAt = new Date(
        scheduledStart.getTime() - event.matchupVisibilityMinutes * 60 * 1000
      );
      if (revealAt.getTime() <= now.getTime()) continue;

      if (!earliestReveal || revealAt.getTime() < earliestReveal.getTime()) {
        earliestReveal = revealAt;
      }
    }

    if (earliestReveal) {
      return { roundNumber, revealAt: earliestReveal };
    }
  }

  return null;
}

/** Total event minutes for social rotate: all rounds plus breaks between them. */
export function computeSocialEventDurationMinutes(
  roundCount: number,
  roundDurationMinutes: number,
  roundBreakMinutes = 0
): number {
  if (roundCount < 1 || roundDurationMinutes < 1) return 0;
  const breaks = Math.max(0, roundCount - 1) * Math.max(0, roundBreakMinutes);
  return roundCount * roundDurationMinutes + breaks;
}

export function formatPlayEventDuration(totalMinutes: number): string {
  if (totalMinutes < 1) return '';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

export function addMinutesToDate(start: Date, totalMinutes: number): Date {
  return new Date(start.getTime() + totalMinutes * 60 * 1000);
}

export function isPublicJoinablePlayEventStatus(status: PlayEventStatus): boolean {
  return (PUBLIC_JOINABLE_PLAY_EVENT_STATUSES as readonly string[]).includes(status);
}

export function computeRoundStartTimes(
  startsAt: Date,
  endsAt: Date,
  roundCount: number,
  roundDurationMinutes?: number | null,
  roundBreakMinutes?: number | null
): Date[] {
  if (roundCount <= 0) return [];
  const times: Date[] = [];
  if (roundDurationMinutes && roundDurationMinutes > 0) {
    const breakMinutes = roundBreakMinutes ?? 0;
    const slotMinutes = roundDurationMinutes + breakMinutes;
    for (let i = 0; i < roundCount; i++) {
      times.push(new Date(startsAt.getTime() + i * slotMinutes * 60 * 1000));
    }
    return times;
  }
  const totalMs = endsAt.getTime() - startsAt.getTime();
  const step = totalMs / roundCount;
  for (let i = 0; i < roundCount; i++) {
    times.push(new Date(startsAt.getTime() + i * step));
  }
  return times;
}
